import React, { useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { ArrowLeft, ArrowRight, MapPin, Calendar, Layers, Users, ArrowUpRight } from "lucide-react"
import { useProject, useProjects } from "../../hooks/useProjects"

export default function WorkDetail() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [activeImage, setActiveImage] = useState(0)

  const { project, loading, error } = useProject(slug)
  const { projects } = useProjects() // for prev/next/related

  // Reset active image when slug changes
  React.useEffect(() => { setActiveImage(0) }, [slug])

  // Loading state
  if (loading) {
    return (
      <main className="bg-stone-950 min-h-screen animate-pulse">
        <div className="h-[70vh] bg-stone-800" />
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-16 space-y-8">
          <div className="h-6 bg-stone-800 w-1/3" />
          <div className="h-4 bg-stone-800 w-2/3" />
          <div className="h-4 bg-stone-800 w-1/2" />
        </div>
      </main>
    )
  }

  // 404 / error
  if (error || !project) {
    return (
      <main className="pt-24 min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="text-center px-6">
          <p className="font-['Cormorant_Garamond'] text-3xl text-stone-600 mb-6">Project not found</p>
          <Link
            to="/works"
            className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-xs tracking-[0.2em] text-amber-400 border border-amber-400 px-6 py-3 hover:bg-amber-400 hover:text-stone-950 transition-all"
          >
            BACK TO WORKS
          </Link>
        </div>
      </main>
    )
  }

  // Previous / next project
  const currentIndex = projects.findIndex((p) => p.slug === slug)
  const prevProject = currentIndex > 0 ? projects[currentIndex - 1] : null
  const nextProject = currentIndex >= 0 && currentIndex < projects.length - 1 ? projects[currentIndex + 1] : null

  // Related — same category, different slug
  const related = projects
    .filter((p) => p.category === project.category && p.slug !== slug)
    .slice(0, 3)

  const gallery = project.gallery || []

  return (
    <main className="bg-stone-950 min-h-screen">
      {/* Hero */}
      <div className="relative h-[70vh] min-h-[480px] overflow-hidden">
        <img
          src={project.hero}
          alt={project.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/40 to-stone-950/20" />

        {/* Back button */}
        <div className="absolute top-24 left-6 lg:left-12 z-10">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.2em] text-stone-400 hover:text-stone-100 transition-colors group"
          >
            <ArrowLeft size={13} className="group-hover:-translate-x-1 transition-transform" />
            BACK
          </button>
        </div>

        {/* Hero content */}
        <div className="absolute bottom-0 left-0 right-0 pb-12 px-6 lg:px-12">
          <div className="max-w-screen-2xl mx-auto">
            <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-4">
              <span className="w-6 h-px bg-amber-400" />
              {project.category}
            </span>
            <h1 className="font-['Cormorant_Garamond'] text-5xl lg:text-7xl font-light text-stone-50 leading-tight">
              {project.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Meta bar */}
      <div className="border-b border-stone-800 bg-stone-900/60">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-5 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: <MapPin size={13} />, label: "Location", value: project.location },
            { icon: <Calendar size={13} />, label: "Year",     value: project.year },
            { icon: <Layers size={13} />,  label: "Area",     value: project.area },
            { icon: <Users size={13} />,   label: "Client",   value: project.client },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3">
              <span className="text-amber-400 mt-0.5 shrink-0">{item.icon}</span>
              <div>
                <p className="font-['Space_Grotesk'] text-[9px] tracking-[0.25em] text-stone-500 uppercase mb-0.5">
                  {item.label}
                </p>
                <p className="font-['Inter'] text-sm text-stone-200">{item.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        {/* Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-20 mb-20">
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-5">
              <span className="w-6 h-px bg-amber-400" />
              Overview
            </span>
            <p className="font-['Inter'] text-base text-stone-300 leading-relaxed">
              {project.description}
            </p>
          </div>
          <div className="space-y-6">
            <div>
              <p className="font-['Space_Grotesk'] text-[9px] tracking-[0.25em] text-stone-500 uppercase mb-2">
                Status
              </p>
              <span className={`inline-block font-['Space_Grotesk'] text-[10px] tracking-[0.15em] px-3 py-1 ${
                project.status === "Completed"
                  ? "bg-emerald-900/40 text-emerald-400 border border-emerald-800"
                  : project.status === "Under Construction"
                  ? "bg-amber-900/40 text-amber-400 border border-amber-800"
                  : "bg-stone-800 text-stone-400 border border-stone-700"
              }`}>
                {(project.status || "").toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-['Space_Grotesk'] text-[9px] tracking-[0.25em] text-stone-500 uppercase mb-3">
                Services
              </p>
              <div className="flex flex-wrap gap-2">
                {(project.services || []).map((s) => (
                  <span key={s} className="font-['Space_Grotesk'] text-[9px] tracking-[0.15em] text-stone-400 border border-stone-700 px-3 py-1.5">
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="font-['Space_Grotesk'] text-[9px] tracking-[0.25em] text-stone-500 uppercase mb-3">
                Tags
              </p>
              <div className="flex flex-wrap gap-2">
                {(project.tags || []).map((t) => (
                  <span key={t} className="font-['Space_Grotesk'] text-[9px] tracking-[0.15em] text-stone-600 border border-stone-800 px-3 py-1.5">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Gallery */}
        {gallery.length > 0 && (
          <div className="mb-20">
            {/* Main image */}
            <div className="overflow-hidden h-[60vh] min-h-[400px] mb-3 cursor-zoom-in">
              <img
                src={gallery[activeImage]}
                alt={`${project.title} — image ${activeImage + 1}`}
                className="w-full h-full object-cover transition-all duration-500"
              />
            </div>
            {/* Thumbnails */}
            <div className="grid grid-cols-4 gap-3">
              {gallery.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`overflow-hidden h-24 lg:h-32 transition-all duration-200 ${
                    i === activeImage
                      ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-stone-950"
                      : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Challenge + Approach */}
        {(project.challenge || project.approach) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24 border-t border-stone-800 pt-16">
            {project.challenge && (
              <div>
                <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-5">
                  <span className="w-6 h-px bg-amber-400" />
                  The Challenge
                </span>
                <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed">
                  {project.challenge}
                </p>
              </div>
            )}
            {project.approach && (
              <div>
                <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-5">
                  <span className="w-6 h-px bg-amber-400" />
                  Our Approach
                </span>
                <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed">
                  {project.approach}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Related projects */}
        {related.length > 0 && (
          <div className="border-t border-stone-800 pt-16 mb-16">
            <div className="flex items-end justify-between mb-10">
              <h2 className="font-['Cormorant_Garamond'] text-3xl lg:text-4xl font-light text-stone-100">
                Related Projects
              </h2>
              <Link
                to="/works"
                className="hidden lg:inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.2em] text-stone-500 hover:text-amber-400 transition-colors group"
              >
                ALL WORKS <ArrowUpRight size={13} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.slug} to={`/works/${p.slug}`} className="group">
                  <div className="overflow-hidden h-56 mb-4">
                    <img
                      src={p.thumbnail}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <span className="font-['Space_Grotesk'] text-[9px] tracking-[0.3em] text-amber-400 uppercase block mb-1">
                    {p.category} — {p.year}
                  </span>
                  <h3 className="font-['Cormorant_Garamond'] text-xl font-light text-stone-100 group-hover:text-amber-200 transition-colors">
                    {p.title}
                  </h3>
                  <p className="font-['Inter'] text-xs text-stone-500 mt-0.5">{p.location}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Prev / Next navigation */}
        <div className="flex flex-col sm:flex-row justify-between gap-4 border-t border-stone-800 pt-10">
          {prevProject ? (
            <Link
              to={`/works/${prevProject.slug}`}
              className="group flex items-center gap-4 hover:gap-5 transition-all"
            >
              <ArrowLeft size={16} className="text-stone-500 group-hover:text-amber-400 transition-colors shrink-0" />
              <div>
                <p className="font-['Space_Grotesk'] text-[9px] tracking-[0.2em] text-stone-600 uppercase mb-1">Previous</p>
                <p className="font-['Cormorant_Garamond'] text-xl font-light text-stone-300 group-hover:text-stone-100 transition-colors">
                  {prevProject.title}
                </p>
              </div>
            </Link>
          ) : <div />}

          {nextProject ? (
            <Link
              to={`/works/${nextProject.slug}`}
              className="group flex items-center gap-4 justify-end hover:gap-5 transition-all sm:text-right"
            >
              <div>
                <p className="font-['Space_Grotesk'] text-[9px] tracking-[0.2em] text-stone-600 uppercase mb-1">Next</p>
                <p className="font-['Cormorant_Garamond'] text-xl font-light text-stone-300 group-hover:text-stone-100 transition-colors">
                  {nextProject.title}
                </p>
              </div>
              <ArrowRight size={16} className="text-stone-500 group-hover:text-amber-400 transition-colors shrink-0" />
            </Link>
          ) : <div />}
        </div>
      </div>
    </main>
  )
}
